import { api, type Page } from "@/lib/api/client";
import type {
  CancelInput,
  CancelRemainingInput,
  ConfirmInput,
  ConfirmPreview,
  ConvertInput,
  FulfilmentFilters,
  FulfilmentOrder,
  FulfilmentOrderDetail,
  LineCreateInput,
  LineFilters,
  LineUpdateInput,
  Order,
  OrderCreateInput,
  OrderFilters,
  OrderLine,
  OrderLineUpdateInput,
  OrderShipment,
  OrderUpdateInput,
  Quotation,
  QuotationCreateInput,
  QuotationFilters,
  QuotationLine,
  QuotationTransitionInput,
  QuotationUpdateInput,
  ReservePreview,
} from "./sales.types";

/**
 * Satışın HTTP katmanı. Tek sorumluluğu yol ve gövde.
 *
 * HARD RULE: burada hiçbir tutar HESAPLANMAZ. Satır toplamı, belge toplamı, KDV ve kademe
 * seçimi sunucudan geliyor ve olduğu gibi taşınıyor. İstemcide bir toplama yazmak, müşteriye
 * gösterilen rakamla denetlenecek rakabın ayrışabilmesi demektir.
 *
 * `unitPrice` gövdede İSTEĞE BAĞLI: verilmezse sunucu fiyat listesinden çözer ve cevabın
 * `priceSource` alanı nereden geldiğini söyler.
 */
export const salesApi = {
  quotations: {
    list(filters: QuotationFilters): Promise<Page<Quotation>> {
      return api.page<Quotation>("sales/quotations", { params: { ...filters } });
    },
    get(id: string): Promise<Quotation> {
      return api.get<Quotation>(`sales/quotations/${id}`);
    },
    create(body: QuotationCreateInput): Promise<Quotation> {
      return api.post<Quotation>("sales/quotations", body);
    },
    update(id: string, body: QuotationUpdateInput): Promise<Quotation> {
      return api.patch<Quotation>(`sales/quotations/${id}`, body);
    },
    /**
     * Durum geçişi ayrı bir çağrı, `update` üzerinde bir alan değil: gönderme kuru donduruyor,
     * red gerekçe istiyor — dördü farklı olaylar.
     */
    transition(id: string, body: QuotationTransitionInput): Promise<Quotation> {
      return api.post<Quotation>(`sales/quotations/${id}/transition`, body);
    },
    convert(id: string, body: ConvertInput): Promise<Order> {
      return api.post<Order>(`sales/quotations/${id}/convert`, body);
    },
  },

  quotationLines: {
    list(quotationId: string, filters: LineFilters): Promise<Page<QuotationLine>> {
      return api.page<QuotationLine>(`sales/quotations/${quotationId}/lines`, {
        params: { ...filters },
      });
    },
    add(quotationId: string, body: LineCreateInput): Promise<QuotationLine> {
      return api.post<QuotationLine>(`sales/quotations/${quotationId}/lines`, body);
    },
    update(quotationId: string, lineId: string, body: LineUpdateInput): Promise<QuotationLine> {
      return api.patch<QuotationLine>(`sales/quotations/${quotationId}/lines/${lineId}`, body);
    },
    remove(quotationId: string, lineId: string): Promise<void> {
      return api.delete(`sales/quotations/${quotationId}/lines/${lineId}`);
    },
  },

  orders: {
    list(filters: OrderFilters): Promise<Page<Order>> {
      return api.page<Order>("sales/orders", { params: { ...filters } });
    },
    get(id: string): Promise<Order> {
      return api.get<Order>(`sales/orders/${id}`);
    },
    create(body: OrderCreateInput): Promise<Order> {
      return api.post<Order>("sales/orders", body);
    },
    update(id: string, body: OrderUpdateInput): Promise<Order> {
      return api.patch<Order>(`sales/orders/${id}`, body);
    },
    /**
     * Onay ÖN İZLEMESİ — `GET`, hiçbir şey değiştirmiyor.
     *
     * Kullanıcı "onayla"ya basmadan önce ne olacağını görmeli; bunu göstermeyen bir onay,
     * sonucun ancak geri alınamaz hâle geldikten sonra öğrenilmesi demektir.
     */
    preview(id: string): Promise<ConfirmPreview> {
      return api.get<ConfirmPreview>(`sales/orders/${id}/confirm-preview`);
    },
    confirm(id: string, body: ConfirmInput): Promise<Order> {
      return api.post<Order>(`sales/orders/${id}/confirm`, body);
    },
    cancel(id: string, body: CancelInput): Promise<Order> {
      return api.post<Order>(`sales/orders/${id}/cancel`, body);
    },
    remove(id: string): Promise<void> {
      return api.delete(`sales/orders/${id}`);
    },
    /** Siparişin stok belgeleri — sevkler ve ters kayıtları (satis-duzeltme 20 Ekim 2026). */
    shipments(id: string): Promise<OrderShipment[]> {
      return api.get<OrderShipment[]>(`sales/orders/${id}/shipments`);
    },
    /**
     * KALANI İPTAL ET — kısmen sevk edilmiş siparişin sevk edilmeyen miktarı gerekçeyle kapanır.
     * `@Idempotent`: anahtar çağıranın `useSubmissionKey`'inden, başarıdan sonra yenileniyor.
     */
    cancelRemaining(id: string, body: CancelRemainingInput, idempotencyKey: string): Promise<Order> {
      return api.post<Order>(`sales/orders/${id}/cancel-remaining`, body, { idempotencyKey });
    },
    /** «Eksik satırları şimdi rezerve et» önizlemesi — `GET`, hiçbir şey değiştirmiyor (S-9). */
    reservePreview(id: string): Promise<ReservePreview> {
      return api.get<ReservePreview>(`sales/orders/${id}/reserve-preview`);
    },
    reserve(id: string): Promise<ReservePreview> {
      return api.post<ReservePreview>(`sales/orders/${id}/reserve`, {});
    },
  },

  orderLines: {
    list(orderId: string, filters: LineFilters): Promise<Page<OrderLine>> {
      return api.page<OrderLine>(`sales/orders/${orderId}/lines`, { params: { ...filters } });
    },
    add(orderId: string, body: LineCreateInput): Promise<OrderLine> {
      return api.post<OrderLine>(`sales/orders/${orderId}/lines`, body);
    },
    /** S-8: taslak satırı düzelt (miktar, indirim; yetkiyle fiyat). */
    update(orderId: string, lineId: string, body: OrderLineUpdateInput): Promise<OrderLine> {
      return api.patch<OrderLine>(`sales/orders/${orderId}/lines/${lineId}`, body);
    },
    remove(orderId: string, lineId: string): Promise<void> {
      return api.delete(`sales/orders/${orderId}/lines/${lineId}`);
    },
  },

  /**
   * DEPO: SEVK BEKLEYEN SİPARİŞLER (S-1). Anahtar `inventory.shipment.manage`; cevapta fiyat yok.
   * Sevk belgesinin kendisi stok belgesi uçlarından açılıyor (`sourceType: "SALES_ORDER"`).
   */
  fulfilment: {
    list(filters: FulfilmentFilters): Promise<Page<FulfilmentOrder>> {
      return api.page<FulfilmentOrder>("sales/fulfilment/orders", { params: { ...filters } });
    },
    get(id: string): Promise<FulfilmentOrderDetail> {
      return api.get<FulfilmentOrderDetail>(`sales/fulfilment/orders/${id}`);
    },
  },
};
